/**
 * 与 ImageCropDetailPage「重新裁剪」/ processCropImages 相同的整批裁剪流程。
 * 列表创建任务、详情页重新裁剪共用此模块。
 *
 * T3-2.1：submitImageCropTask（唯一服务端入队路径）→ pollImageCropBatch 轮询 → 更新任务进度 → 结果缓存。
 * T3-2.2：轮询/间隔 sleep 中检查 isImageCropTaskCancelled，取消后停止 poll/submit 并重置未终态行为 pending。
 * T3-2.3：保留 shouldProcessImageCrop 跳过逻辑——已成功/已失败且非 forceReprocess 时不 submit。
 */
import { cropImageAPI, taskAPI } from '@/utils/api';
import { loadCropImagesForTask } from '../utils/imageCropLoadResults';
import {
  clearImageCropTaskCancelled,
  isImageCropTaskCancelled,
} from '../utils/imageCropTaskCancel';
import { resolveImageCropOriginalUrl } from '../utils/imageCropResolveUrl';
import { rememberImageCropResult } from '../utils/imageCropResultCache';
import { computeCropTaskProgress } from '../utils/imageCropTaskStats';
import {
  buildCropImageSavePayload,
  mapCropRowToDetailRow,
  normalizeCropRowStatus,
  shouldProcessImageCrop,
} from '../utils/imageCropRowStatus';
import { IMAGE_CROP_WORKER_MODE, IMAGE_CROP_POLL_INTERVAL_MS } from '../constants/imageCropConfig';
import { submitImageCropTask } from './imageCropTaskApi';
import {
  fetchAllImageCropPollRowMap,
  sleepImageCropPollInterval,
} from '../utils/imageCropPoll';
import {
  isImageCropFatalPollError,
  presentImageCropApiError,
} from '../utils/imageCropTaskApiError';

/** 防止 poll 异常时 while(true) 失控；约 20 分钟上限 */
const IMAGE_CROP_POLL_MAX_TICKS = 600;

const syncChains = new Map();

function enqueueTaskSync(taskId, fn) {
  const key = String(taskId);
  const prev = syncChains.get(key) || Promise.resolve();
  const next = prev.then(fn, fn);
  syncChains.set(key, next);
  next.finally(() => {
    if (syncChains.get(key) === next) syncChains.delete(key);
  });
  return next;
}

async function sleepUnlessCancelled(taskId, ms) {
  return sleepImageCropPollInterval(ms, () => isImageCropTaskCancelled(taskId));
}

function buildCancelledResult(onImageProgress, resetImageIds = []) {
  for (const imageId of resetImageIds) {
    onImageProgress?.(imageId, { status: 'pending' });
  }
  return { finished: true, cancelled: true };
}

function collectNonTerminalImageIds(imageIds, rowMap) {
  return imageIds.filter((id) => !isCropRowTerminal(rowMap.get(String(id))));
}

function isCropRowTerminal(row) {
  const st = normalizeCropRowStatus(row);
  return st !== 'pending' && st !== 'processing';
}

function areAllImageIdsTerminal(rowMap, imageIds) {
  for (const id of imageIds) {
    if (!isCropRowTerminal(rowMap.get(String(id)))) return false;
  }
  return true;
}

async function fetchAllPollRows(taskId) {
  return fetchAllImageCropPollRowMap(taskId, {
    shouldAbort: () => isImageCropTaskCancelled(taskId),
  });
}

/** @deprecated 请用 computeCropTaskProgress */
export function deriveImageCropTaskStatus(total, successCount, failedCount, allProcessed) {
  if (!allProcessed || total <= 0) return 'processing';
  if (successCount > 0 && failedCount > 0) return 'partial';
  if (failedCount >= total) return 'failed';
  return 'success';
}

/** 与详情页 convertToBase64DataUrl 一致 */
export async function convertImageUrlForCrop(imageUrl) {
  if (!imageUrl) return '';
  if (imageUrl.startsWith('data:')) return imageUrl;
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) return imageUrl;
  if (imageUrl.startsWith('uploads/')) {
    return `/${imageUrl.replace(/^\/+/, '')}`;
  }
  if (imageUrl.startsWith('/uploads/')) {
    return imageUrl;
  }
  if (imageUrl.startsWith('blob:')) {
    const response = await fetch(imageUrl);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }
  return imageUrl;
}

function emitImageRowProgress(taskId, imageId, row, originalUrl, onImageProgress) {
  const detail = mapCropRowToDetailRow(imageId, row, originalUrl);
  onImageProgress?.(imageId, {
    status: detail.status,
    croppedUrl: detail.croppedUrl,
    error: detail.error,
    pipeline: detail.pipeline,
    isQwenBbox: detail.isQwenBbox,
    cropPosition: detail.cropPosition,
    cropPositionDisplay: detail.cropPositionDisplay,
    usedFallback: detail.usedFallback,
  });
  if (detail.status === 'success' && detail.croppedUrl) {
    rememberImageCropResult(taskId, imageId, {
      status: 'success',
      croppedUrl: detail.croppedUrl,
      error: null,
    });
  }
}

/**
 * @param {object} options
 * @param {string} options.taskId
 * @param {object} [options.taskMeta] - { id, subAccount, taskId }
 * @param {string[]} options.imageIds
 * @param {(imageId: string) => Promise<string>} [options.resolveOriginalUrl]
 * @param {boolean} [options.forceReprocess] - 同详情「重新裁剪」
 * @param {boolean} [options.skipServerSubmit] - 服务端 Worker 已在跑时仅轮询、不重复 submit
 * @param {(imageId: string, patch: object) => void} [options.onImageProgress]
 * @param {(patch: object) => void} [options.onTaskProgress]
 * @param {(message: string, type?: string) => void} [options.showToast]
 */
export async function runImageCropTaskBatch({
  taskId,
  taskMeta,
  imageIds,
  resolveOriginalUrl,
  forceReprocess = false,
  skipServerSubmit = false,
  onImageProgress,
  onTaskProgress,
  showToast,
}) {
  const ids = [...new Set((imageIds || []).filter(Boolean).map(String))];
  const total = ids.length;
  const meta = taskMeta || { id: taskId };
  const resolve = resolveOriginalUrl || ((id) => resolveImageCropOriginalUrl(id));

  if (forceReprocess) {
    clearImageCropTaskCancelled(taskId);
  }

  const emitTask = (patch) => {
    onTaskProgress?.(patch);
  };

  if (total === 0) {
    await taskAPI.update(taskId, { status: 'failed', successCount: 0, failedCount: 0, total: 0 });
    emitTask({ total: 0, successCount: 0, failedCount: 0, status: 'failed' });
    return { finished: true, cancelled: false };
  }

  if (isImageCropTaskCancelled(taskId)) {
    return buildCancelledResult(onImageProgress);
  }

  const existing = await loadCropImagesForTask(meta, ids);
  if (isImageCropTaskCancelled(taskId)) {
    return buildCancelledResult(onImageProgress);
  }
  const existingMap = new Map();
  (existing || []).forEach((row) => {
    if (row?.imageId != null) existingMap.set(String(row.imageId), row);
  });

  const items = [];
  for (const imageId of ids) {
    if (isImageCropTaskCancelled(taskId)) {
      return buildCancelledResult(onImageProgress, items.map((item) => item.imageId));
    }

    const row = existingMap.get(imageId);
    if (!shouldProcessImageCrop(row, { forceReprocess })) continue;

    let originalUrl = (row?.originalImageUrl && String(row.originalImageUrl).trim()) || '';
    if (!originalUrl) {
      originalUrl = await resolve(imageId);
      if (isImageCropTaskCancelled(taskId)) {
        return buildCancelledResult(onImageProgress, items.map((item) => item.imageId));
      }
    }
    items.push({ imageId, originalUrl });
  }

  const syncProgress = () =>
    enqueueTaskSync(taskId, async () => {
      const cropImages = await loadCropImagesForTask(meta, ids);
      const progress = computeCropTaskProgress(cropImages, ids);
      const status = progress.pendingCount > 0 ? 'processing' : progress.status;
      await taskAPI.update(taskId, {
        total: progress.total,
        successCount: progress.successCount,
        failedCount: progress.failedCount,
        status,
      });
      emitTask({
        total: progress.total,
        successCount: progress.successCount,
        failedCount: progress.failedCount,
        status,
      });
      return {
        successCount: progress.successCount,
        failedCount: progress.failedCount,
        status,
        allProcessed: progress.pendingCount <= 0,
      };
    });

  if (items.length === 0) {
    const fin = await syncProgress();
    return { finished: true, cancelled: false, ...fin };
  }

  const submitImages = [];
  for (const imgData of items) {
    if (isImageCropTaskCancelled(taskId)) {
      return buildCancelledResult(
        onImageProgress,
        collectNonTerminalImageIds(submitImages.map((img) => img.imageId), existingMap),
      );
    }

    if (!imgData.originalUrl) {
      onImageProgress?.(imgData.imageId, {
        status: 'failed',
        error: '原图地址缺失，无法处理',
      });
      await cropImageAPI.save(
        taskId,
        buildCropImageSavePayload({
          imageId: imgData.imageId,
          status: 'failed',
          error: '原图地址缺失，无法处理',
        }),
      );
      await syncProgress();
      continue;
    }

    const imageUrlForCrop = await convertImageUrlForCrop(imgData.originalUrl);
    if (isImageCropTaskCancelled(taskId)) {
      return buildCancelledResult(
        onImageProgress,
        collectNonTerminalImageIds(submitImages.map((img) => img.imageId), existingMap),
      );
    }
    if (!imageUrlForCrop) {
      onImageProgress?.(imgData.imageId, {
        status: 'failed',
        error: '原图地址无效，无法处理',
      });
      await cropImageAPI.save(
        taskId,
        buildCropImageSavePayload({
          imageId: imgData.imageId,
          originalImageUrl: imgData.originalUrl,
          status: 'failed',
          error: '原图地址无效，无法处理',
        }),
      );
      await syncProgress();
      continue;
    }

    submitImages.push({
      imageId: imgData.imageId,
      imageUrl: imageUrlForCrop,
      originalUrl: imgData.originalUrl,
    });
  }

  if (submitImages.length > 0) {
    for (const img of submitImages) {
      onImageProgress?.(img.imageId, { status: 'processing' });
    }

    if (isImageCropTaskCancelled(taskId)) {
      return buildCancelledResult(
        onImageProgress,
        submitImages.map((img) => img.imageId),
      );
    }

    if (!skipServerSubmit) {
      try {
        await submitImageCropTask({
          taskId,
          images: submitImages.map(({ imageId, imageUrl }) => ({ imageId, imageUrl })),
          mode: IMAGE_CROP_WORKER_MODE,
          force: forceReprocess,
        }, { showToast });
      } catch (submitError) {
        const presented = presentImageCropApiError(submitError, { showToast: undefined });
        const failMsg = presented?.message || '处理失败';
        for (const img of submitImages) {
          onImageProgress?.(img.imageId, { status: 'failed', error: failMsg });
          await cropImageAPI.save(
            taskId,
            buildCropImageSavePayload({
              imageId: img.imageId,
              originalImageUrl: img.originalUrl,
              status: 'failed',
              error: failMsg,
            }),
          );
        }
        await syncProgress();
        throw presented;
      }
    }

    const originalUrlById = new Map(
      submitImages.map((img) => [String(img.imageId), img.originalUrl]),
    );

    let pollTicks = 0;
    while (true) {
      if (isImageCropTaskCancelled(taskId)) {
        return buildCancelledResult(onImageProgress, submitImages.map((img) => img.imageId));
      }

      let polledMap;
      try {
        polledMap = await fetchAllPollRows(taskId);
      } catch (pollError) {
        if (isImageCropFatalPollError(pollError)) {
          for (const img of submitImages) {
            onImageProgress?.(img.imageId, { status: 'failed', error: '处理失败' });
            await cropImageAPI.save(
              taskId,
              buildCropImageSavePayload({
                imageId: img.imageId,
                originalImageUrl: img.originalUrl,
                status: 'failed',
                error: '处理失败',
              }),
            );
          }
          await syncProgress();
          throw presentImageCropApiError(pollError, { showToast: undefined });
        }
        // 短暂网络错误：有限次重试
        pollTicks += 1;
        if (pollTicks >= IMAGE_CROP_POLL_MAX_TICKS) {
          for (const img of submitImages) {
            onImageProgress?.(img.imageId, { status: 'failed', error: '处理超时' });
          }
          await syncProgress();
          throw pollError;
        }
        const shouldContinueAfterError = await sleepUnlessCancelled(taskId, IMAGE_CROP_POLL_INTERVAL_MS);
        if (!shouldContinueAfterError) {
          return buildCancelledResult(
            onImageProgress,
            collectNonTerminalImageIds(submitImages.map((img) => img.imageId), existingMap),
          );
        }
        continue;
      }

      if (polledMap === null) {
        return buildCancelledResult(onImageProgress, submitImages.map((img) => img.imageId));
      }

      const mergedMap = new Map(existingMap);
      polledMap.forEach((row, key) => mergedMap.set(key, row));

      for (const img of submitImages) {
        const row = mergedMap.get(String(img.imageId));
        if (row) {
          emitImageRowProgress(
            taskId,
            img.imageId,
            row,
            originalUrlById.get(String(img.imageId)) || '',
            onImageProgress,
          );
        }
      }

      await syncProgress();

      if (areAllImageIdsTerminal(mergedMap, ids)) {
        break;
      }

      pollTicks += 1;
      if (pollTicks >= IMAGE_CROP_POLL_MAX_TICKS) {
        for (const img of submitImages) {
          if (!isCropRowTerminal(mergedMap.get(String(img.imageId)))) {
            onImageProgress?.(img.imageId, { status: 'failed', error: '处理超时' });
          }
        }
        await syncProgress();
        break;
      }

      const shouldContinue = await sleepUnlessCancelled(taskId, IMAGE_CROP_POLL_INTERVAL_MS);
      if (!shouldContinue) {
        return buildCancelledResult(
          onImageProgress,
          collectNonTerminalImageIds(submitImages.map((img) => img.imageId), mergedMap),
        );
      }
    }
  }

  const cropImages = await loadCropImagesForTask(meta, ids);
  const progress = computeCropTaskProgress(cropImages, ids);

  await taskAPI.update(taskId, {
    status: progress.status,
    total: progress.total,
    successCount: progress.successCount,
    failedCount: progress.failedCount,
  });
  emitTask({
    total: progress.total,
    successCount: progress.successCount,
    failedCount: progress.failedCount,
    status: progress.status,
  });

  return {
    finished: true,
    cancelled: false,
    successCount: progress.successCount,
    failedCount: progress.failedCount,
    status: progress.status,
  };
}
